import { isAfter, startOfDay } from 'date-fns'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from '@wartek-id/collapsible-v1'
import { CollapsibleIcon } from '@wartek-id/collapsible'

import { InvalidUserNoAccessPelaksanaan } from 'api/kinerja/kepala-sekolah/config'
import {
  API_PATH_PREDIKAT_KINERJA_STEP,
  useAPIGetPelaksanaanConfig,
  useAPIGetPenetapanPredikatKinerjaStep,
  useAPIGetPredikatKinerjaList,
  useAPIUpsertAdditionalPredikatKinerja,
} from 'api/kinerja/useAPIKinerja'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { SectionPanduanPenetapanPredikatKinerja } from 'app/KinerjaPage/pages/PelaksanaanV2/components/PenetapanPredikatKinerja/SectionPanduanPenetapanPredikatKinerja'
import { TablePenilaianKinerja } from 'app/KinerjaPage/pages/PelaksanaanV2/components/TablePenilaianKinerja'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import { PENETAPAN_PREDIKAT_KINERJA_STEPS } from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import { isDateValid } from 'utils/datetime'
import { assetUrl } from 'utils/url'
import { CardCollapsible } from 'app/KinerjaShared/components/CardCollapsible'

import type { PredikatKinerjaItem } from 'types/kinerja-pelaksanaan'

type PenetapanPredikatKinerjaSusulanProps = {
  npsn: string
}

export function PenetapanPredikatKinerjaSusulan({
  npsn,
}: PenetapanPredikatKinerjaSusulanProps) {
  const isSubmitGuruEnabled =
    process.env.NEXT_PUBLIC_SKP_FORM_F_SUBMIT_GURU_ENABLED === 'true'

  const router = useRouter()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [items, setItems] = useState<Array<PredikatKinerjaItem>>([])
  const [activeDialog, setActiveDialog] = useState<
    'NONE' | 'SUBMISSION' | 'GUIDE'
  >('NONE')

  const {
    data: dataPelaksanaanConfig,
    isSuccess: isSuccessPelaksanaanConfig,
    isFetching: isLoadingPelaksanaanConfig,
  } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isPenetapanPredikatKinerjaStarted = useMemo(() => {
    // Deadline date
    const startDate =
      dataPelaksanaanConfig?.data?.predicatePerformanceAccessDate
    const isValidDate = isDateValid(startDate)
    const isStarted =
      isValidDate && isAfter(new Date(), startOfDay(new Date(startDate)))

    return isStarted
  }, [dataPelaksanaanConfig])

  const {
    data: dataPenetapanStep,
    isSuccess: isSuccessPenetapanStep,
    isLoading: isLoadingPenetapanStep,
  } = useAPIGetPenetapanPredikatKinerjaStep({
    params: {
      npsn,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      enabled: isPenetapanPredikatKinerjaStarted,
    },
  })

  // Submission step-related values
  const currentSubmissionStep = dataPenetapanStep?.data?.submissionStep
  const currentSubmissionStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS[currentSubmissionStep]?.order ?? 0
  const submitGuruStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS['SUBMIT_GURU'].order
  const isCorrectStep = currentSubmissionStepOrder > submitGuruStepOrder // Should've been sent to Guru

  const isAllowed = isCorrectStep && isPenetapanPredikatKinerjaStarted

  const { isFetching: isLoadingGetList, data: dataGetList } =
    useAPIGetPredikatKinerjaList({
      params: { npsn },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        enabled: isAllowed && isSuccessPenetapanStep,
      },
    })

  const upsertAdditionalPredikatKinerjaMutation =
    useAPIUpsertAdditionalPredikatKinerja(npsn)

  // List-related values
  const isLoadingAllData =
    isLoadingPelaksanaanConfig || isLoadingPenetapanStep || isLoadingGetList

  // Summary-related values
  const isSubmitDisabled =
    upsertAdditionalPredikatKinerjaMutation.isLoading ||
    isLoadingAllData ||
    items.length === 0

  // Visually disable the button while keeping it clickable to show toast messages
  const isSubmitPartialDisabled = isSubmitDisabled || !isSubmitGuruEnabled

  const handleSubmission = () => {
    if (!isSubmitDisabled && !isSubmitPartialDisabled) {
      upsertAdditionalPredikatKinerjaMutation.mutate(
        {
          data: items.map((item) => ({
            ptkId: item.ptkId,
            workBehavior: item.workBehavior?.result,
            performancePractice: item.performancePractice?.result,
          })),
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(
              `${API_PATH_PREDIKAT_KINERJA_STEP}/${npsn}`
            )
            toast({
              message: 'Berhasil kirim ke guru!',
              type: 'success',
            })

            router.replace(
              `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}`
            )
          },
          onError: () => {
            setActiveDialog('NONE')

            toast({
              message: 'Gagal kirim ke guru. Coba lagi.',
            })

            router.replace(
              `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}`
            )
          },
        }
      )
    }
  }

  // Sync remote data with local state
  useEffect(() => {
    // Only show item that is allowed to resubmit, eventhough the submission step in the current NPSN is sent to Guru
    // E.g. Guru affected by mutasi/rotasi
    setItems(
      dataGetList?.data?.completedUser?.filter(
        (item) => item.ableSubmit === true
      ) ?? []
    )
  }, [dataGetList])

  // Redirect to hub if the step is incorrect
  useEffect(() => {
    if (
      isSuccessPenetapanStep &&
      isSuccessPelaksanaanConfig &&
      !isAllowed &&
      !upsertAdditionalPredikatKinerjaMutation.isSuccess // Make sure this doesnt run upon cache invalidation
    ) {
      router.replace(
        `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}`
      )
      toast({
        message: 'Lakukan sesuai urutan, ya.',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAllowed,
    isSuccessPenetapanStep,
    upsertAdditionalPredikatKinerjaMutation.isSuccess,
  ])

  // Show no access error if the Penetapan date is not started
  useEffect(() => {
    if (!isPenetapanPredikatKinerjaStarted && isSuccessPelaksanaanConfig) {
      throw new InvalidUserNoAccessPelaksanaan('path', null)
    }
  }, [isPenetapanPredikatKinerjaStarted, isSuccessPelaksanaanConfig])

  return (
    <>
      <main className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
        <div className="max-w-7xl mx-auto space-y-5">
          <Header
            title="Penetapan Predikat Kinerja Pegawai Baru"
            className="mb-9"
            rightNode={
              <Button
                variant="ghost"
                className="space-x-1 !bg-white"
                size="lg"
                onClick={() => setActiveDialog('GUIDE')}
              >
                <Icon color="inherit">help</Icon>
                <span>Panduan Penetapan</span>
              </Button>
            }
          />

          <section>
            <div className="rounded-t-lg border bg-white px-5 pt-6 pb-4 space-y-4">
              <div className="flex items-start space-x-6">
                <div className="w-full space-y-1">
                  <Text variant="heading-md" as="h2">
                    Daftar Penetapan Predikat Kinerja
                  </Text>
                  <Text color="subdued">
                    Langsung kirim ke guru atau sesuaikan dengan mengubah Rating
                    Praktik Kinerja dan Rating Perilaku Kerja.
                  </Text>
                </div>

                <div className="space-x-3 flex flex-shrink-0 items-start">
                  <Button
                    className="space-x-1"
                    size="lg"
                    disabled={isSubmitDisabled}
                    aria-disabled={isSubmitPartialDisabled}
                    onClick={() => {
                      if (!isSubmitGuruEnabled) {
                        toast({
                          message:
                            'Mohon maaf, belum dapat lanjut karena halaman baru tersedia di bulan Juni.',
                        })
                      } else {
                        setActiveDialog('SUBMISSION')
                      }
                    }}
                  >
                    <Icon color="inherit">lock</Icon>
                    <span>Kirim ke guru</span>
                  </Button>
                </div>
              </div>
            </div>

            <TablePenilaianKinerja
              isLoading={isLoadingAllData}
              items={items}
              hasScrollDownButton={false}
              onChange={(items) => {
                setItems(items)
              }}
            />
          </section>
          <section>
            <CardCollapsible>
              <CollapsibleTrigger type="button">
                <div className="space-x-3 flex completedItems-center">
                  <Icon>info_outlined</Icon>
                  <Text variant="heading-sm" as="h2">
                    Perlu diketahui bahwa:
                  </Text>
                </div>
                <CollapsibleIcon />
              </CollapsibleTrigger>
              <CollapsibleContent className="p-4">
                <ol className="list-decimal pl-14">
                  <Text as="li" color="subdued">
                    Anda tidak perlu menyelesaikan penilaian dan penetapan untuk
                    guru yang sudah tidak aktif (pensiun, meninggal dunia,
                    mutasi, dsb.) atau nama yang terduplikasi.
                  </Text>
                  <Text as="li" color="subdued">
                    Jika Anda ingin memperbarui data, mohon kunjungi Pusat
                    Bantuan.
                  </Text>
                </ol>
              </CollapsibleContent>
            </CardCollapsible>
          </section>
        </div>
      </main>

      <SubmitConfirmationDialog
        isOpen={activeDialog === 'SUBMISSION'}
        isSubmitting={upsertAdditionalPredikatKinerjaMutation.isLoading}
        onClose={() => {
          setActiveDialog('NONE')
        }}
        onSubmit={handleSubmission}
      />

      <Dialog
        isOpen={activeDialog === 'GUIDE'}
        size="lg"
        onClose={() => setActiveDialog('NONE')}
      >
        <DialogOverlay />
        <DialogContent>
          <SectionPanduanPenetapanPredikatKinerja
            actionNode={
              <Button type="button" onClick={() => setActiveDialog('NONE')}>
                Lanjut ke Penetapan
              </Button>
            }
            npsn={npsn}
            className="bg-white rounded-lg p-6"
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

type SubmitConfirmationDialogProps = {
  isOpen: boolean
  isSubmitting: boolean
  onClose: () => void
  onSubmit: () => void
}

const CONFIRMATION_TEXT =
  'Saya setuju untuk mengirim Predikat Kinerja ke guru dan mengerti bahwa Predikat Kinerja tidak dapat diubah lagi.'

function SubmitConfirmationDialog({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}: SubmitConfirmationDialogProps) {
  const [confirmation, setConfirmation] = useState('')

  const isConfirmed = confirmation === CONFIRMATION_TEXT

  // Reset confirmation state upon unmounting
  useEffect(() => {
    return () => {
      setConfirmation('')
    }
  }, [])

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        <div className="p-6 space-y-4">
          <img
            src={assetUrl('/illustrations/img-publish-data.png')}
            alt=""
            width={84}
            height={84}
          />

          <Text variant="display-sm" as="h2">
            Predikat Kinerja tidak dapat diubah lagi setelah dikirim. Lanjut
            kirim ke guru?
          </Text>

          <Text variant="body-lg" color="subdued">
            Setelah dikirim, Predikat Kinerja tidak dapat diubah lagi dan guru
            akan menerima Dokumen Evaluasi Kinerja.
          </Text>
          <Text variant="body-lg" color="subdued">
            Mohon ketik kalimat ini pada kolom kalimat persetujuan:
          </Text>
          <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
            <Text variant="heading-sm" color="inherit">
              {CONFIRMATION_TEXT}
            </Text>
          </div>
          <div className="space-y-1">
            <Text as="label" htmlFor="confirmation-text">
              Kalimat persetujuan
            </Text>
            <Textarea
              id="confirmation-text"
              placeholder="Ketik di sini."
              autoComplete="off"
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>

          <div className="flex space-x-3 !justify-start">
            <Button color="white" size="lg" onClick={onClose}>
              Kembali
            </Button>
            <Button
              color="blue"
              disabled={!isConfirmed || isSubmitting}
              size="lg"
              type="button"
              isLoading={isSubmitting}
              onClick={() => onSubmit?.()}
            >
              Kirim
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
